import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  BookOpen, 
  Phone, 
  ArrowRight, 
  FileText, 
  CheckCircle2,
  ExternalLink,
  Star,
  DollarSign,
} from 'lucide-react';
import Seo from '../components/Seo';

const PHONE_NUMBER = "254-624-3308";
const HOME_HEALTH_BOOK_LINK = "https://a.co/d/041VSz1V";
const BILL_EXPENSE_JOURNAL_LINK = "https://a.co/d/07klu4E8";
const BOOK_COVER_IMAGE = "https://customer-assets.emergentagent.com/job_ba288840-867e-4483-82f4-5d28fd248d68/artifacts/c26khoui_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2004_58_41%20PM.png";
const BILL_JOURNAL_COVER = "https://customer-assets.emergentagent.com/job_1371e3dd-2e8c-44cd-85da-e4d8bbde3121/artifacts/fejrhoe4_61V0lWt-SML._SL1293_.jpg";
const OPS_PLANNER_LINK = "https://www.amazon.com/dp/B0GYFBWRMD";
const OPS_PLANNER_COVER = "https://m.media-amazon.com/images/I/61MDlj8rnSL._SY342_.jpg";
const RAISING_DAUGHTERS_LINK = "https://www.amazon.com/dp/B0GZZWZ3C9";
const RAISING_DAUGHTERS_COVER = "https://m.media-amazon.com/images/I/71OqaRUC2xL._SY342_.jpg";

const Resources = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title="Tools & Resources for Social Workers"
        description="Professional tools and resources for social workers in home health, hospice and clinical settings — books, planners and practical guides curated for the field."
        path="/resources"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-[#0F2E24] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <BookOpen className="w-5 h-5 text-secondary" />
            <span className="text-secondary font-medium">Resources for Social Workers</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4">
            Tools & Resources
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Professional resources designed specifically for social workers in home health, hospice, and clinical settings.
          </p>
        </div>
      </section>

      {/* Books Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              Books & Planners by DSWS Creative Press
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Stay organized and in control with our beautifully designed planners and journals. From professional tools for social workers to personal finance trackers for everyone.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Home Health & Hospice Travel Log */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 flex justify-center">
                <img 
                  src={BOOK_COVER_IMAGE}
                  alt="Home Health & Hospice Travel Log"
                  className="h-64 object-contain rounded-lg shadow-md"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm text-gray-600">Best Seller</span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3">
                  Home Health & Hospice Travel Log
                </h3>
                <p className="text-gray-600 mb-4">
                  Beautiful pampas design with enough pages for the entire year! Perfect for tracking mileage, documenting visits, managing expenses, and staying organized.
                </p>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Daily Travel Log & Mileage Tracker</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Expense Records & Reimbursements</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Contact Directory & Monthly Calendar</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Patient Visit Documentation</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Respite Stay Tracking</span>
                  </div>
                </div>

                <a 
                  href={HOME_HEALTH_BOOK_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-secondary hover:bg-secondary-hover text-white" data-testid="home-health-book-btn">
                    Shop on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Monthly Bill Expense Journal */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-8 flex justify-center">
                <img 
                  src={BILL_JOURNAL_COVER}
                  alt="Monthly Bill Expense Journal"
                  className="h-64 object-contain rounded-lg shadow-md"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-pink-600" />
                  <span className="text-sm text-gray-600">Personal Finance • 8x10 Full Pages</span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3">
                  Monthly Bill Expense Journal
                </h3>
                <p className="text-gray-600 mb-4">
                  Take control of your money with clarity, confidence, and intention. A clean, practical budget book designed to help you track expenses, manage bills, and stay organized month after month.
                </p>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Monthly Bills & Due Dates Tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Expense & Balance Management</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Insurance & Subscription Tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Utilities & Recurring Payments</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm">Clean Layout with Ample Writing Space</span>
                  </div>
                </div>

                <a 
                  href={BILL_EXPENSE_JOURNAL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button className="w-full bg-pink-600 hover:bg-pink-700 text-white" data-testid="bill-journal-btn">
                    Shop on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            {/* The Small Business Operations Planner */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-8 flex justify-center">
                <img src={OPS_PLANNER_COVER} alt="The Small Business Operations Planner" className="h-64 object-contain rounded-lg shadow-md" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-gray-600">Hardcover • $34.08</span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3">
                  The Small Business Operations Planner
                </h3>
                <p className="text-gray-600 mb-4">
                  A complete day-to-day record system for startup entrepreneurs & growing businesses — combining business setup, daily operations, financial tracking, and tax prep in one structured book.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Business Setup & Daily Operations</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Income, Expense & Financial Tracking</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Tax Preparation Organizer</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">185 Pages • 7.24 x 10.24 in</span></div>
                </div>
                <a href={OPS_PLANNER_LINK} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ops-planner-btn">
                    Shop on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Raising Daughters with Accountability */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 flex justify-center">
                <img src={RAISING_DAUGHTERS_COVER} alt="Raising Daughters with Accountability" className="h-64 object-contain rounded-lg shadow-md" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm text-gray-600">Hardcover • $24.48</span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3">
                  Raising Daughters with Accountability
                </h3>
                <p className="text-gray-600 mb-4">
                  Correction with Sanity, Truth, and Love. A thoughtful guide to strengthening the mother–daughter bond through accountability, honest communication, and grace — growth without shame.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Healthy Communication & Accountability</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Correction Without Condemnation</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">Grounded in Biblical Wisdom & Real Insight</span></div>
                  <div className="flex items-center gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm">For Mothers, Daughters & Families</span></div>
                </div>
                <a href={RAISING_DAUGHTERS_LINK} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="raising-daughters-btn">
                    Shop on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Bulk Orders Info */}
          <div className="mt-12 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 border border-primary/10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-serif text-xl font-bold text-gray-900 mb-2">
                  Bulk Orders for Your Team?
                </h3>
                <p className="text-gray-600">
                  Employers: Equip your social work team with professional planners. Contact us for bulk pricing and custom branding options.
                </p>
              </div>
              <a href={`tel:${PHONE_NUMBER}`}>
                <Button className="bg-primary hover:bg-primary-hover text-white whitespace-nowrap" data-testid="bulk-order-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Call {PHONE_NUMBER}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-gray-900 mb-8 text-center">
            More Resources
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/jobs" className="group">
              <div className="bg-gray-50 rounded-xl p-6 hover:bg-primary hover:text-white transition-all">
                <FileText className="w-8 h-8 text-primary group-hover:text-white mb-4" />
                <h3 className="font-semibold text-lg mb-2">Browse Jobs</h3>
                <p className="text-gray-600 group-hover:text-gray-200 text-sm">
                  Find your next social work opportunity from our curated job board.
                </p>
              </div>
            </Link>

            <Link to="/employer-faq" className="group">
              <div className="bg-gray-50 rounded-xl p-6 hover:bg-primary hover:text-white transition-all">
                <BookOpen className="w-8 h-8 text-primary group-hover:text-white mb-4" />
                <h3 className="font-semibold text-lg mb-2">Employer FAQ</h3>
                <p className="text-gray-600 group-hover:text-gray-200 text-sm">
                  Learn about license verification and hiring best practices.
                </p>
              </div>
            </Link>

            <Link to="/contact" className="group">
              <div className="bg-gray-50 rounded-xl p-6 hover:bg-primary hover:text-white transition-all">
                <Phone className="w-8 h-8 text-primary group-hover:text-white mb-4" />
                <h3 className="font-semibold text-lg mb-2">Contact Us</h3>
                <p className="text-gray-600 group-hover:text-gray-200 text-sm">
                  Have questions? We're here to help. Call {PHONE_NUMBER}.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary to-[#0F2E24]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Start Your Job Search?
          </h2>
          <p className="text-gray-300 mb-8">
            Join thousands of social workers who have found their dream jobs through INeedASocialWorker.com
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/jobs">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8" data-testid="browse-jobs-cta">
                Browse Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary px-8" data-testid="create-account-cta">
                Create Free Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
